import { useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import { BASE_URL } from "@config/apiConfig";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import {
  emiratesIdNumberSchema,
  passportNumberSchema,
} from "@features/port-security/schema/portSecurityFormSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { paths } from "@utils/navigation";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CHECK_PAGE_LANGUAGE_ICON_SRC,
  CHECK_PAGE_LOGO_SRC,
  LANG_BASE,
  TEST_ID_BASE,
} from "./constants";
import { useCheckPublicSecurityClearance } from "./hooks/useCheckPublicSecurityClearance";
import {
  getCheckSecurityClearanceSchema,
  type CheckSecurityClearanceFormValues,
} from "./schema/checkSecurityClearanceSchema";
import {
  formatSecurityClearanceExpirationDate,
  getSecurityClearanceStatusLabelKey,
  resolveSecurityClearanceCheckResult,
  type SecurityClearanceCheckMessageKind,
} from "./utils/securityClearanceCheckStatus";

const CheckSecurityClearancePage = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const year = new Date().getFullYear();

  const schema = useMemo(() => getCheckSecurityClearanceSchema(t), [t]);
  const passportLabel = t(`${LANG_BASE}.passport-number`);
  const emiratesIdLabel = t(`${LANG_BASE}.emirates-id-no`);

  const form = useForm<CheckSecurityClearanceFormValues>({
    mode: "onTouched",
    reValidateMode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: { passportNumber: "", emiratesIdNumber: "" },
  });

  const passportNumber = form.watch("passportNumber") ?? "";
  const emiratesIdNumber = form.watch("emiratesIdNumber") ?? "";
  const [messageKind, setMessageKind] = useState<SecurityClearanceCheckMessageKind | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);

  const checkStatus = useCheckPublicSecurityClearance();

  const footerLinks = [
    { label: t(`${LANG_BASE}.public-page.faq`), href: "#" },
    { label: t(`${LANG_BASE}.public-page.terms-and-conditions`), href: "#" },
    { label: t(`${LANG_BASE}.public-page.privacy-policy`), href: "#" },
  ];

  const clearResult = () => {
    setMessageKind(null);
    setSubmissionStatus(null);
    setExpirationDate(null);
  };

  const clearPassportNumber = () => {
    form.setValue("passportNumber", "", { shouldDirty: true });
    form.clearErrors("passportNumber");
    clearResult();
  };

  const clearEmiratesIdNumber = () => {
    form.setValue("emiratesIdNumber", "", { shouldDirty: true });
    form.clearErrors("emiratesIdNumber");
    clearResult();
  };

  const handleCheck = async () => {
    const passport = String(form.getValues("passportNumber") ?? "").trim();
    const emiratesId = String(form.getValues("emiratesIdNumber") ?? "").trim();

    if (!passport && !emiratesId) {
      clearResult();
      return;
    }

    if (passport) {
      const result = passportNumberSchema(10, passportLabel).safeParse(passport);
      if (!result.success) {
        form.setError("passportNumber", {
          type: "manual",
          message: result.error.issues[0]?.message ?? t("form-validation.invalid-passport"),
        });
        clearResult();
        return;
      }
      form.clearErrors("passportNumber");
    }

    if (emiratesId) {
      const result = emiratesIdNumberSchema(emiratesIdLabel).safeParse(emiratesId);
      if (!result.success) {
        form.setError("emiratesIdNumber", {
          type: "manual",
          message: result.error.issues[0]?.message ?? t("form-validation.invalid-emirates-id"),
        });
        clearResult();
        return;
      }
      form.clearErrors("emiratesIdNumber");
    }

    clearResult();

    try {
      const data = await checkStatus.mutateAsync({
        id: passport || undefined,
        emiratesId: emiratesId || undefined,
      });
      const result = resolveSecurityClearanceCheckResult(data);
      setMessageKind(result.kind);
      setSubmissionStatus(result.status ?? null);
      setExpirationDate(
        result.kind === "approved"
          ? formatSecurityClearanceExpirationDate(data?.expirationDate)
          : null,
      );
    } catch {
      setMessageKind("not_found");
      setSubmissionStatus(null);
      setExpirationDate(null);
    }
  };

  const renderStatusMessage = () => {
    if (!messageKind) return null;

    const statusLabel =
      submissionStatus != null
        ? t(getSecurityClearanceStatusLabelKey(submissionStatus), {
            defaultValue: submissionStatus.replace(/_/g, " "),
          })
        : "";

    const messageConfig: Record<
      SecurityClearanceCheckMessageKind,
      { color: string; Icon: typeof CheckCircleOutlineIcon; text: string }
    > = {
      approved: {
        color: theme.palette.success.main,
        Icon: CheckCircleOutlineIcon,
        text: t(`${LANG_BASE}.passport-no-or-emirates-id-approved`, {
          date: expirationDate ?? "",
        }),
      },
      rejected_suspended: {
        color: theme.palette.error.main,
        Icon: HighlightOffIcon,
        text: t(`${LANG_BASE}.passport-no-or-emirates-id-rejected`),
      },
      not_found: {
        color: theme.palette.warning.main,
        Icon: WarningAmberIcon,
        text: t(`${LANG_BASE}.passport-no-or-emirates-id-not-found`),
      },
      other_status: {
        color: theme.palette.warning.main,
        Icon: WarningAmberIcon,
        text: t(`${LANG_BASE}.passport-no-or-emirates-id-with-status`, {
          status: statusLabel,
        }),
      },
    };

    const { color, Icon, text } = messageConfig[messageKind];

    return (
      <div
        data-testid={`${TEST_ID_BASE}-status-message`}
        className="flex flex-row items-center"
        style={{ color }}
      >
        <Icon className="mr-2" />
        <p>{text}</p>
      </div>
    );
  };

  return (
    <div
      dir={dir}
      data-testid={`${TEST_ID_BASE}-page`}
      className="min-h-screen bg-white flex flex-col"
    >
      <header className="flex items-center justify-between px-8 lg:px-24 pt-3 lg:pt-8 pb-0">
        <Link
          to={paths.checkSecurityClearance}
          className="flex items-center shrink-0"
          data-testid={`${TEST_ID_BASE}-logo`}
        >
          <img
            src={CHECK_PAGE_LOGO_SRC.mobile}
            alt={t("licensing.marhub-logo-alt")}
            className="block sm:hidden h-8 w-auto"
            data-testid={`${TEST_ID_BASE}-logo-mobile`}
          />
          <img
            src={CHECK_PAGE_LOGO_SRC.desktop}
            alt={t("licensing.marhub-logo-alt")}
            className="hidden sm:block h-8 w-auto"
            data-testid={`${TEST_ID_BASE}-logo-desktop`}
          />
        </Link>

        <div
          data-testid={`${TEST_ID_BASE}-language-toggle`}
          aria-label="Language"
          className="p-0.5"
        >
          <img
            src={CHECK_PAGE_LANGUAGE_ICON_SRC}
            alt=""
            className="h-9 w-auto"
            data-testid={`${TEST_ID_BASE}-language-toggle-icon`}
          />
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-8 lg:px-24 pt-6 pb-12">
        <FormProvider {...form}>
          <div className="w-full max-w-[420px] flex flex-col items-stretch">
            <h3 className="text-[28px] font-medium text-[#2F3441] text-center mb-3">
              {t(`${LANG_BASE}.check-sec-clearance`)}
            </h3>
            <p className="text-sm text-[#646A77] text-center mb-8">
              {t(`${LANG_BASE}.public-page.subtitle`)}
            </p>

            <div className="flex flex-col">
              <FormItem<CheckSecurityClearanceFormValues, "passportNumber">
                name="passportNumber"
                type="text"
                label={t(`${LANG_BASE}.passport-no`)}
                testId={`${TEST_ID_BASE}-passportNumber`}
                value={passportNumber}
                onFieldChange={(_, value) => {
                  if (value === "") {
                    form.clearErrors("passportNumber");
                    clearResult();
                  }
                }}
                onFocus={clearEmiratesIdNumber}
                enforceUppercase
              />

              <FormItem<CheckSecurityClearanceFormValues, "emiratesIdNumber">
                name="emiratesIdNumber"
                type="text"
                label={t(`${LANG_BASE}.emirates-id`)}
                inputLabel={t(`${LANG_BASE}.emirates-id-label`)}
                testId={`${TEST_ID_BASE}-emiratesIdNumber`}
                value={emiratesIdNumber}
                onFieldChange={(_, value) => {
                  if (value === "") {
                    form.clearErrors("emiratesIdNumber");
                    clearResult();
                  }
                }}
                onFocus={clearPassportNumber}
                formatOnChange={formatOnChangeEmiratesIDNumber}
              />

              {renderStatusMessage()}

              <div className="mt-4 flex flex-col gap-4">
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => void handleCheck()}
                  disabled={(!passportNumber && !emiratesIdNumber) || checkStatus.isPending}
                  testId={`${TEST_ID_BASE}-check-button`}
                  label={<span className="normal-case font-medium">{t(`${LANG_BASE}.check`)}</span>}
                />

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => window.location.assign(BASE_URL)}
                  testId={`${TEST_ID_BASE}-back-button`}
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  sx={{
                    "backgroundColor": "#F3F4F6",
                    "color": "#2F3441",
                    "&:hover": { backgroundColor: "#E5E7EB" },
                  }}
                  label={
                    <span className="normal-case font-medium">
                      {t(`${LANG_BASE}.public-page.back-to-login`)}
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </FormProvider>
      </main>

      <footer
        data-testid={`${TEST_ID_BASE}-footer`}
        className="px-8 lg:px-24 pt-6 pb-3 lg:pb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between text-xs text-[#646A77]"
      >
        <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {footerLinks.map((link) => (
            <a key={link.label} href={link.href} className="hover:underline">
              {link.label}
            </a>
          ))}
        </nav>
        <p className="text-[#646A77]">{t(`${LANG_BASE}.public-page.copyright`, { year })}</p>
      </footer>
    </div>
  );
};

export default CheckSecurityClearancePage;
